import {Component, OnInit, HostListener} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ReactiveFormsModule, FormBuilder, FormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {TableModule} from 'primeng/table';
import {ButtonModule} from 'primeng/button';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {MenuModule} from 'primeng/menu';
import {TooltipModule} from 'primeng/tooltip';
import {Farm} from '@/common/interface/farm.data.model';
import {FarmService} from '@/services/farm.service';
import {FilterConfig} from '@/components/search-filter/search-filter.component';
import {HasRoleDirective} from '@/has-role.directive';
@Component({
    selector: 'app-farms-list',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TableModule,
        ButtonModule,
        DropdownModule,
        InputTextModule,
        MenuModule,
        TooltipModule,
        HasRoleDirective
    ],
    templateUrl: './farms-list.component.html',
    styleUrl: './farms-list.component.scss'
})
export class FarmsListComponent implements OnInit {
    farms: Farm[] = [];
    filteredFarms: Farm[] = [];
    loading = false;
    userDetail: any;
    activeActionMenu: number | null = null;

    farmFilterForm: FormGroup;

    farmFilterConfig: FilterConfig = {
        fields: [
            {
                key: 'name',
                label: 'Farm Name',
                type: 'text',
                placeholder: 'Enter farm name'
            },
            {
                key: 'address',
                label: 'Address',
                type: 'text',
                placeholder: 'Enter address'
            }
        ]
    };

    // Menu items for farm actions
    farmMenuItems = [
        {
            label: 'View Details',
            icon: 'fas fa-eye',
            command: (event: any) => this.viewFarmDetails(event.item.data)
        },
        {
            label: 'Edit Farm',
            icon: 'fas fa-edit',
            command: (event: any) => this.editFarm(event.item.data)
        }
    ];

    constructor(
        private farmService: FarmService,
        private router: Router,
        private fb: FormBuilder
    ) {
        this.farmFilterForm = this.fb.group({
            name: [''],
            address: ['']
        });
    }

    ngOnInit(): void {
        this.userDetail = JSON.parse(
            localStorage.getItem('UserDetail') || '{}'
        );
        if (this.userDetail?.id) {
            this.loadFarms();
        }
    }

    loadFarms(): void {
        this.loading = true;
        this.farmService.getFarmDashboardList(this.userDetail.id).subscribe({
            next: (response) => {
                this.farms = response || [];
                this.filteredFarms = [...this.farms];
                this.loading = false;
            },
            error: (error) => {
                console.error('Error loading farms:', error);
                this.farms = [];
                this.filteredFarms = [];
                this.loading = false;
            }
        });
    }

    onFarmFilterApplied(filterData: any): void {
        this.filteredFarms = this.farms.filter((farm) => {
            let matches = true;

            if (filterData.name) {
                matches =
                    matches &&
                    farm.name
                        .toLowerCase()
                        .includes(filterData.name.toLowerCase());
            }

            if (filterData.address) {
                matches =
                    matches &&
                    farm.address
                        .toLowerCase()
                        .includes(filterData.address.toLowerCase());
            }

            return matches;
        });
    }

    onFarmFilterCleared(): void {
        this.filteredFarms = [...this.farms];
        this.farmFilterForm.reset();
    }

    filterFarms(): void {
        const filterValue = this.farmFilterForm.value;
        this.onFarmFilterApplied(filterValue);
    }

    addNewFarm(): void {
        this.router.navigate(['/add-farm']);
    }

    viewFarmDetails(farm: Farm): void {
        this.router.navigate(['/farm-detail', farm.id]);
    }

    editFarm(farm: Farm): void {
        this.router.navigate(['/edit-farm', farm.id]);
    }

    getTotalHouses(): number {
        return this.farms.reduce(
            (total, farm) => total + (farm.farmHouses?.length || 0),
            0
        );
    }

    getTotalPartitions(): number {
        return this.farms.reduce((total, farm) => {
            return (
                total +
                    farm.farmHouses?.reduce((houseTotal, house) => {
                        return (
                            houseTotal +
                            (house.farmHousePartitions?.length || 0)
                        );
                    }, 0) || 0
            );
        }, 0);
    }

    getAverageSize(): number {
        if (this.farms.length === 0) return 0;
        const totalSize = this.farms.reduce(
            (total, farm) => total + farm.size,
            0
        );
        return Math.round(totalSize / this.farms.length);
    }

    getFarmPartitionsCount(farm: Farm): number {
        if (!farm.farmHouses || farm.farmHouses.length === 0) {
            return 0;
        }

        return farm.farmHouses.reduce((total, house) => {
            return total + (house.farmHousePartitions?.length || 0);
        }, 0);
    }

    toggleActionsMenu(farmId: number): void {
        this.activeActionMenu =
            this.activeActionMenu === farmId ? null : farmId;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        const target = event.target as HTMLElement;
        if (!target.closest('.smart-actions-dropdown')) {
            this.activeActionMenu = null;
        }
    }
}
